"use client";

import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  TextField,
  Autocomplete,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { Colors } from "../../Assets/Styles/Colors";
import { useEffect, useState } from "react";
import { FilterAlt, CalendarToday, Person } from "@mui/icons-material";
import moment from "moment";
import ReportServices from "../../Api/Report/Report.index";
import SimpleDialog from "../../Components/Dialog/Dialog";
import UserServices from "../../Api/UserServices/User.index";
import { Delete as DeleteIcon } from "@mui/icons-material";
import Storage from "../../Utils/Storage";
import { jwtDecode } from "jwt-decode";

function DataFilter({
  sortData,
  onDateChange,
  selectedDate,
  setSelectedDate,
  selectedUser,
  setSelectedUser,
}) {
  const [filterCollapse, setFilterCollapse] = useState(true);
  const [names, setNames] = useState([]);
  const [usersSearch, setUsersSearch] = useState("");

  const { setStorageItem, getStorageItem } = Storage();
  const [user, setUser] = useState(getStorageItem("user"));

  const decodedToken = jwtDecode(user.token);
  const userIdFromToken = decodedToken.id;
  const company_id = userIdFromToken;

  const filterData = async () => {
    if (!selectedDate || !selectedUser) {
      console.error("Both date and user must be selected");
      return;
    }

    const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
    console.log("Filtering data for:", formattedDate, "User:", selectedUser);

    try {
      const data = await sortData(selectedUser, "", formattedDate);
      console.log("Filtered Data:", data);
    } catch (error) {
      console.error("Error during filtering:", error);
    }
  };

  const clearData = () => {
    sortData("", "", "");
    setUsersSearch("");
    setSelectedUser("");
    setSelectedDate("");
  };

  const handleGetUsers = async (page, limit, search, status, companyId) => {
    try {
      const { data, responseCode, message } = await UserServices.getUser(
        page,
        limit,
        search,
        status ? status : "",
        companyId ? companyId : company_id
      );
      const filteredName = data?.output?.users.filter(
        (email) => email.role !== "admin" && email.role !== "company"
      );
      setNames(filteredName);
      console.log(data);
    } catch (error) {
      console.error("Error while fetching users:", error);
    }
  };

  const handleUserChange = (event, value) => {
    setUsersSearch(value?._id);
    setSelectedUser(value?._id);
    console.log("Selected user:", value?._id);
  };

  useEffect(() => {
    handleGetUsers(1, 1000, "", "", company_id);
  }, []);

  return (
    <Box sx={{ mx: 2 }}>
      <Box
        component={Paper}
        sx={{
          bgcolor: Colors.white,
          p: 3,
          borderRadius: "16px",
          mt: 4,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            fontWeight="600"
            sx={{
              color: Colors.primary,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FilterAlt />
            Filters
          </Typography>
          <IconButton
            onClick={() => setFilterCollapse(!filterCollapse)}
            sx={{
              bgcolor: Colors.primaryLight,
              color: Colors.primary,
              "&:hover": { bgcolor: Colors.primary, color: "white" },
            }}
          >
            <FilterAlt />
          </IconButton>
        </Box>

        <Collapse in={filterCollapse}>
          <Grid container spacing={3} justifyContent="space-between">
            <Grid item xs={12} md={6}>
              <InputLabel
                sx={{ mb: 1, fontWeight: "600", color: Colors.primary }}
              >
                <Person sx={{ mr: 1, fontSize: "18px" }} />
                Select User *
              </InputLabel>
              <Autocomplete
                options={names}
                getOptionLabel={(option) => option.name || ""}
                value={names.find((name) => name._id === selectedUser) || null}
                onChange={handleUserChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Choose a user..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        "&:hover fieldset": {
                          borderColor: Colors.primary,
                        },
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputLabel
                sx={{ mb: 1, fontWeight: "600", color: Colors.primary }}
              >
                <CalendarToday sx={{ mr: 1, fontSize: "18px" }} />
                Select Date *
              </InputLabel>
              <TextField
                type="date"
                fullWidth
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  // onDateChange(e.target.value)
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    "&:hover fieldset": {
                      borderColor: Colors.primary,
                    },
                  },
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button
                onClick={clearData}
                sx={{
                  color: Colors.primary,
                  textTransform: "capitalize",
                  minWidth: "120px",
                  borderRadius: "12px",
                  border: `2px solid ${Colors.primary}`,
                  "&:hover": {
                    bgcolor: Colors.primaryLight,
                  },
                }}
              >
                Clear Filters
              </Button>
              <Button
                onClick={filterData}
                variant="contained"
                disabled={!selectedDate || !selectedUser}
                sx={{
                  textTransform: "capitalize",
                  minWidth: "120px",
                  borderRadius: "12px",
                  bgcolor: Colors.primary,
                  "&:hover": {
                    bgcolor: Colors.primary,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": {
                    bgcolor: "#ccc",
                  },
                }}
              >
                Search Data
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Box>
    </Box>
  );
}

function MobileCard({ user, projects, selectedDate, onViewDetails }) {
  const theme = useTheme();

  return (
    <Card
      className="mobile-card"
      sx={{
        mb: 2,
        borderRadius: "16px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        },
        transition: "all 0.3s ease",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* User Info */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="600" color={Colors.primary}>
            {user?.user_name}
          </Typography>

          <Chip
            label={moment(selectedDate).format("MMM DD, YYYY")}
            color="primary"
            size="small"
            sx={{ borderRadius: "8px" }}
          />
        </Box>

        {/* Projects List */}
        {projects.map((project, idx) => {
          const dayData = project?.days?.find(
            (day) => moment(day.date).format("YYYY-MM-DD") === selectedDate
          );

          return (
            <Box
              key={idx}
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: "12px",
                bgcolor: Colors.primaryLight,
              }}
            >
              <Typography
                variant="body1"
                fontWeight="600"
                color={Colors.primary}
              >
                {project?.project_name?.name}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Supervisor:{" "}
                {project?.project_name?.assignedSupervisor || "Not Assigned"}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1.5,
                }}
              >
                <Typography variant="body2" fontWeight="600">
                  Hours Worked:
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="700"
                  color={Colors.primary}
                >
                  {dayData?.hour || 0} hrs
                </Typography>
              </Box>
              <Box
                sx={{
                
                  mt: 1.5,
                }}
              >
                <Typography variant="body2" fontWeight="600">
                  Comments:
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="normal"
                  fontSize={"14px"}
                  color={Colors.primary}
                >
                  {dayData?.comments || "-"} 
                </Typography>
              </Box>
            </Box>
          );
        })}

        {/* Single Button at the bottom */}
        <Button
          variant="outlined"
          onClick={() => {
            const firstProject = projects[0];
            const dayData = firstProject?.days?.find(
              (day) => moment(day.date).format("YYYY-MM-DD") === selectedDate
            );
            onViewDetails(
              dayData,
              firstProject?.project_name?.name,
              dayData?.hour
            );
          }}
          sx={{
            borderRadius: "8px",
            textTransform: "capitalize",
            borderColor: Colors.primary,
            display: "flex",
            margin: "0 auto",
            justifyContent: "center",
            color: Colors.primary,
            minWidth: "120px",
            "&:hover": {
              backgroundColor: Colors.primaryLight,
            },
          }}
        >
          View Details
        </Button>
      </CardContent>
    </Card>
  );
}

const TableComponent = () => {
  const [statsData, setStatsData] = useState([]);
  const [usersSearch, setUsersSearch] = useState("");
  const [projectsSearch, setProjectsSearch] = useState("");
  const [hour, setHour] = useState("");
  const [comments, setComments] = useState("");
  const [submissions, setSubmissions] = useState("");
  const [projectName, setProjectName] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    setSelectedDate(today);
    sortByStatus("", "", today); // all users, all projects, current date
  }, []);

  const sortByStatus = (usersSearch, projectsSearch, formattedDate) => {
    setUsersSearch(usersSearch);
    setProjectsSearch(projectsSearch);
    if (usersSearch || projectsSearch || formattedDate) {
      setHasSearched(true);
      GetStatsReport(
        usersSearch ? usersSearch : "",
        projectsSearch ? projectsSearch : "",
        formattedDate
      );
    } else {
      setStatsData([]);
      setHasSearched(false);
    }
  };

  const GetStatsReport = async (usersSearch, projectsSearch, formattedDate) => {
    setLoading(true);
    try {
      console.log(
        "Fetching stats for date:",
        formattedDate,
        "User:",
        usersSearch
      );

      const { result } = await ReportServices.getStats(
        usersSearch || "",
        projectsSearch || "",
        formattedDate || ""
      );

      console.log("API Result:", result);
      setStatsData(result || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
      setStatsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleDialog = (hours, projectName, hour) => {
    console.log(hours, projectName, hour)
    setOpenDialog(true);
    setComments(hours.comments);
    setSubmissions(hours.submissions);
    setProjectName(projectName);
    setHour(hours.hoursList);
  };

  const handleDeleteComment = (index) => {
    setCommentToDelete(index);
    setOpenConfirmDialog(true);
  };

  const confirmDeleteComment = () => {
    if (commentToDelete !== null) {
      const updatedComments = comments.filter((_, i) => i !== commentToDelete);
      setComments(updatedComments);
      setOpenConfirmDialog(false);
    }
  };

  return (
    <>
      <Box sx={{ mt: 5, ml: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: "700",
            fontSize: { xs: "24px", md: "32px" },
            color: Colors.primary,
            mb: 1,
          }}
        >
          Users Single Day Report
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a user and date to view detailed work reports
        </Typography>
      </Box>

      <DataFilter
        onDateChange={handleDateChange}
        setSelectedDate={setSelectedDate}
        selectedDate={selectedDate}
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        sortData={(usersSearch, projectsSearch, formattedDate) =>
          sortByStatus(usersSearch, projectsSearch, formattedDate)
        }
        usersSearch={usersSearch}
        projectsSearch={projectsSearch}
      />

      <Box sx={{ mt: 3, mx: 2, mb: 12 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: Colors.primary }} size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading data...
            </Typography>
          </Box>
        ) : !hasSearched ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              fontWeight="600"
              color="text.secondary"
              textAlign="center"
            >
              No Data Found
            </Typography>
            {/* <Typography variant="body1" color="text.secondary" textAlign="center">
              Please select a user and date
            </Typography> */}
          </Box>
        ) : statsData?.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "300px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography variant="h5" fontWeight="600" color="text.secondary">
              No Data Found
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              No work data found for the selected date and user
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Box className="dcwwewecvw">
                {statsData?.map((user, index) => {
                  const filteredProjects = user?.projects?.filter((project) =>
                    project?.days?.some(
                      (day) =>
                        moment(day.date).format("YYYY-MM-DD") ===
                          selectedDate && day.hour > 0
                    )
                  );

                  if (filteredProjects?.length > 0) {
                    return (
                      <MobileCard
                        key={index}
                        user={user}
                        projects={filteredProjects} // 👈 pass all projects together
                        selectedDate={selectedDate}
                        onViewDetails={handleDialog}
                      />
                    );
                  }

                  return null; // skip if no projects
                })}
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: "16px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    mt: 3,
                  }}
                >
                  <Table sx={{ minWidth: 650 }} aria-label="user stats table">
                    {/* Table Header */}
                    <TableHead>
                      <TableRow sx={{ backgroundColor: Colors.primaryLight }}>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                          }}
                        >
                          User Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                          }}
                        >
                          Project Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                          }}
                        >
                          Supervisor
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                          }}
                        >
                          Date
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                          }}
                        >
                          Hours Worked
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "600",
                            background: Colors.primary,
                            border: "1px solid #ddd",
                            textAlign:'center'
                          }}
                        >
                          Comments
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    {/* Table Body */}
                    <TableBody>
  {statsData?.map((user, index) => {
    const filteredProjects = user?.projects?.filter(
      (project) =>
        project?.days?.some(
          (day) =>
            moment(day.date).format("YYYY-MM-DD") === selectedDate &&
            day.hour > 0
        )
    );

    return filteredProjects?.map((project, projIndex) => {
      const dayData = project?.days?.find(
        (day) =>
          moment(day.date).format("YYYY-MM-DD") === selectedDate
      );

      return (
        <TableRow key={`${index}-${projIndex}`}>
          {/* User Name ek hi row me dikhana */}
          {projIndex === 0 && (
            <TableCell
              rowSpan={filteredProjects.length}
              align="center"
              sx={{
                border: "1px solid #ddd",
                fontWeight: "500",
                verticalAlign: "middle",
              }}
            >
              {user?.user_name}
            </TableCell>
          )}

          <TableCell sx={{ border: "1px solid #ddd" }}>
            {project?.project_name?.name}
          </TableCell>
          <TableCell sx={{ border: "1px solid #ddd" }}>
            {project?.project_name?.assignedSupervisor || "Not Assigned"}
          </TableCell>
          <TableCell sx={{ border: "1px solid #ddd" }}>
            {moment(selectedDate).format("MMM DD, YYYY")}
          </TableCell>
          <TableCell
            sx={{
              border: "1px solid #ddd",
              fontWeight: "600",
              color: Colors.primary,
            }}
          >
            {dayData?.hour || 0} hrs
          </TableCell>

          
            <TableCell
             
              align="center"
              sx={{
                border: "1px solid #ddd",
                verticalAlign: "middle",
                fontStyle: "italic",
                color: "#555",
              }}
            >
              {dayData?.comments ? dayData?.comments : "No comment"}
            </TableCell>
          
        </TableRow>
      );
    });
  })}
</TableBody>

                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        )}
      </Box>

      <SimpleDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
        comments={comments}
        title="Comments"
      >
        <Box sx={{ mt: 2, fontWeight: "600" }}>
          {comments?.length > 0 ? (
            <ul>
              {comments.map((item, index) => (
                <li
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    {projectName} - {item === "" ? "No Comments" : item} -{" "}
                    {hour[index] + "hrs"}
                  </span>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteComment(index)}
                    sx={{ marginLeft: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </li>
              ))}
            </ul>
          ) : (
            "No Comments"
          )}
        </Box>
      </SimpleDialog>
      <SimpleDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        title="Confirm Delete"
      >
        <Box sx={{ mt: 2, fontWeight: "600" }}>
          <Typography variant="body1">
            Are you sure you want to delete this comment?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              sx={{
                border: "1px solid black",
                color: Colors.black,
                mr: 2,
              }}
              onClick={() => setOpenConfirmDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={confirmDeleteComment}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </SimpleDialog>
    </>
  );
};

export default TableComponent;
